import axios from 'axios';

// API Base URL - relative path works for both development (proxied) and production (same origin)
const baseApiUrl = '/backend_php/api';

const api = axios.create({
    baseURL: baseApiUrl,
});

// ==========================================
// 1. Core Master Data APIs
// ==========================================

export const fetchUsers = async (params = {}) => {
    const response = await api.get('/users.php', { params });
    return response.data;
};

export const createUser = async (userData) => {
    const response = await api.post('/users.php', userData);
    return response.data;
};

export const updateUser = async (userData) => {
    const response = await api.put('/users.php', userData);
    return response.data;
};

export const deleteUser = async (userId) => {
    const response = await api.delete(`/users.php?id=${userId}`);
    return response.data;
};

export const fetchUserPermissions = async (type = 'role', id = null) => {
    const response = await api.get('/user_permissions.php', { params: { type, id } });
    return response.data;
};

export const saveUserPermissions = async (permissionData) => {
    const response = await api.post('/user_permissions.php', permissionData);
    return response.data;
};

export const fetchTeamPerformance = async (supervisorId = null) => {
    const response = await api.get('/team_performance.php', { params: { supervisor_id: supervisorId } });
    return response.data;
};

export const fetchUsersHierarchy = async (userId = null) => {
    const url = '/users_hierarchy.php' + (userId ? `?user_id=${userId}` : '');
    const response = await api.get(url);
    return response.data;
};

// ==========================================
// 2. Client Management APIs
// ==========================================

export const fetchClients = async () => {
    const response = await api.get('/clients.php');
    return response.data;
};

export const createClient = async (clientData) => {
    const response = await api.post('/clients.php', clientData);
    return response.data;
};

export const updateClient = async (clientData) => {
    const response = await api.put('/clients.php', clientData);
    return response.data;
};

export const deleteClient = async (clientId) => {
    const response = await api.delete(`/clients.php?id=${clientId}`);
    return response.data;
};

// ==========================================
// 3. Products & Rates APIs
// ==========================================

export const fetchProducts = async () => {
    const response = await api.get('/products.php');
    return response.data;
};

export const createProduct = async (productData) => {
    const response = await api.post('/products.php', productData);
    return response.data;
};

export const updateProduct = async (productData) => {
    const response = await api.put('/products.php', productData);
    return response.data;
};

export const deleteProduct = async (productId) => {
    const response = await api.delete(`/products.php?id=${productId}`);
    return response.data;
};

export const fetchRates = async (params = {}) => {
    const response = await api.get('/rates.php', { params });
    return response.data;
};

export const saveRateCard = async (rateData) => {
    const response = await api.post('/rates.php', rateData);
    return response.data;
};

export const deleteRateCard = async (rateId) => {
    const response = await api.delete(`/rates.php?id=${rateId}`);
    return response.data;
};

// ==========================================
// 4. Case Management & Workflow APIs
// ==========================================

export const fetchCases = async (params = {}) => {
    const response = await api.get('/cases.php', { params });
    return response.data;
};

export const deleteCase = async (caseId, permanent = false) => {
    const response = await api.delete(`/cases.php?id=${caseId}${permanent ? '&permanent=1' : ''}`);
    return response.data;
};

export const updateCaseAction = async (caseId, action, data = {}, userId = 'USR001') => {
    const response = await api.post('/cases_update.php', {
        case_id: caseId,
        action,
        user_id: userId,
        ...data
    });
    return response.data;
};

export const bulkCaseAction = async (caseIds, action, data = {}, userId = 'USR001') => {
    const response = await api.post('/cases_bulk.php', {
        case_ids: caseIds,
        action,
        user_id: userId,
        ...data
    });
    return response.data;
};

export const createLead = async (leadData, userId = 'USR001') => {
    const response = await api.post('/lead_creation.php', {
        ...leadData,
        user_id: userId
    });
    return response.data;
};

export const submitMobileVerification = async (caseId, verificationStatus, answers, remarks, latitude = null, longitude = null, userId = 'USR005') => {
    const response = await api.post('/mobile_force_save.php', {
        case_id: caseId,
        verification_status: verificationStatus,
        answers,
        remarks,
        latitude,
        longitude,
        user_id: userId
    });
    return response.data;
};

export const saveGeotag = async (caseId, imageId, latitude, longitude, locationName = '', userId = 'USR001') => {
    const response = await api.post('/geotag_save.php', {
        case_id: caseId,
        image_id: imageId,
        latitude,
        longitude,
        locationName,
        user_id: userId
    });
    return response.data;
};

export const uploadCaseFile = async (caseId, file, type = 'image', label = 'Uploaded File', userId = 'USR001') => {
    const formData = new FormData();
    formData.append('case_id', caseId);
    formData.append('file', file);
    formData.append('type', type);
    formData.append('label', label);
    formData.append('user_id', userId);
    
    const response = await api.post('/upload_handler.php', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
};

export const fetchCaseHistory = async (caseId, userId = 'USR001', role = 'admin') => {
    const response = await api.get(`/case_history.php?case_id=${caseId}&user_id=${userId}&role=${role}`);
    return response.data;
};

export const createAuditLog = async (logData) => {
    const response = await api.post('/case_history.php', logData);
    return response.data;
};

// ==========================================
// 5. Case Deletion & Return Requests
// ==========================================

export const fetchCaseDeletions = async (type = 'requests') => {
    const response = await api.get('/case_deletions.php', { params: { type } });
    return response.data;
};

export const submitCaseDeletion = async (caseId, reason, comment = '', userId = 'USR001') => {
    const response = await api.post('/case_deletions.php', { action: 'request', case_id: caseId, reason, comment, user_id: userId });
    return response.data;
};

export const actionCaseDeletion = async (caseId, approve, comment = '', userId = 'USR001') => {
    const action = approve ? 'approve' : 'reject';
    const response = await api.post('/case_deletions.php', { action, case_id: caseId, comment, user_id: userId });
    return response.data;
};

export const restoreRemovedCase = async (caseId, userId = 'USR001') => {
    const response = await api.post('/case_deletions.php', { action: 'restore', case_id: caseId, user_id: userId });
    return response.data;
};

export const fetchCaseReturns = async (params = {}) => {
    const response = await api.get('/case_returns.php', { params });
    return response.data;
};

export const submitCaseReturn = async (caseId, feId, reason) => {
    const response = await api.post('/case_returns.php', { action: 'submit', case_id: caseId, fe_id: feId, reason });
    return response.data;
};

export const recommendCaseReturn = async (id, supervisorId, remark) => {
    const response = await api.post('/case_returns.php', { action: 'recommend', id, supervisor_id: supervisorId, remark });
    return response.data;
};

export const approveCaseReturn = async (id, remark) => {
    const response = await api.post('/case_returns.php', { action: 'approve', id, remark });
    return response.data;
};

export const rejectCaseReturn = async (id, remark) => {
    const response = await api.post('/case_returns.php', { action: 'reject', id, remark });
    return response.data;
};

// ==========================================
// 6. Quality Control (QC) APIs
// ==========================================

export const fetchQCDashboard = async (qcUserId = null) => {
    const response = await api.get('/qc_dashboard.php', { params: { qc_user_id: qcUserId } });
    return response.data;
};

// ==========================================
// 7. Finance APIs (Invoices, Payouts, Penalties, Waivers, Extra Costs)
// ==========================================

export const fetchInvoices = async (params = {}) => {
    const response = await api.get('/invoices.php', { params });
    return response.data;
};

export const createInvoice = async (invoiceData) => {
    const response = await api.post('/invoices.php', invoiceData);
    return response.data;
};

export const updateInvoiceStatus = async (id, status, paidDate = null) => {
    const response = await api.post('/invoices.php', { action: 'update_status', id, status, paidDate });
    return response.data;
};

export const fetchPayouts = async (params = {}) => {
    const response = await api.get('/payouts.php', { params });
    return response.data;
};

export const submitPayoutRequest = async (payoutData) => {
    const response = await api.post('/payouts.php', payoutData);
    return response.data;
};

export const updatePayoutStatus = async (id, action) => {
    const response = await api.post('/payouts.php', { action, id });
    return response.data;
};

export const fetchPenalties = async (params = {}) => {
    const response = await api.get('/penalties.php', { params });
    return response.data;
};

export const issuePenalty = async (penaltyData) => {
    const response = await api.post('/penalties.php', penaltyData);
    return response.data;
};

export const updatePenaltyStatus = async (id, action) => {
    const response = await api.post('/penalties.php', { action, id });
    return response.data;
};

export const fetchWaiverRequests = async (params = {}) => {
    const response = await api.get('/waiver_requests.php', { params });
    return response.data;
};

export const submitWaiverRequest = async (penaltyId, userId, justification) => {
    const response = await api.post('/waiver_requests.php', { action: 'create', penalty_id: penaltyId, user_id: userId, justification });
    return response.data;
};

export const recommendWaiver = async (id, note, recommendedBy) => {
    const response = await api.post('/waiver_requests.php', { action: 'recommend', id, note, recommended_by: recommendedBy });
    return response.data;
};

export const finalizeWaiver = async (id, decision, approvedBy) => {
    const response = await api.post('/waiver_requests.php', { action: 'finalize', id, decision, approved_by: approvedBy });
    return response.data;
};

export const fetchExtraCosts = async (params = {}) => {
    const response = await api.get('/extra_costs.php', { params });
    return response.data;
};

export const submitExtraCost = async (costData) => {
    const response = await api.post('/extra_costs.php', { action: 'create', ...costData });
    return response.data;
};

export const updateExtraCost = async (id, action, userId = 'USR001') => {
    const response = await api.post('/extra_costs.php', { action, id, user_id: userId });
    return response.data;
};

// ==========================================
// 8. Form Builder & Verification Types
// ==========================================

export const fetchQuestionsConfig = async (verificationTypeId = null) => {
    const url = '/questions_config.php' + (verificationTypeId ? `?verification_type_id=${verificationTypeId}` : '');
    const response = await api.get(url);
    return response.data;
};

export const fetchVerificationTypes = async () => {
    const response = await api.get('/verification_types.php');
    return response.data;
};

export const createVerificationType = async (typeData) => {
    const response = await api.post('/verification_types.php', typeData);
    return response.data;
};

export const fetchFormTemplates = async (params = {}) => {
    const response = await api.get('/form_builder.php', { params });
    return response.data;
};

export const saveFormTemplate = async (templateData) => {
    const response = await api.post('/form_builder.php', templateData);
    return response.data;
};

export const deleteFormTemplate = async (id) => {
    const response = await api.delete(`/form_builder.php?id=${id}`);
    return response.data;
};

// ==========================================
// 9. Dashboard, Reports, Analytics & Notifications
// ==========================================

export const fetchDashboardStats = async () => {
    const response = await api.get('/dashboard_stats.php');
    return response.data;
};

export const fetchActivityFeed = async () => {
    const response = await api.get('/activity_feed.php');
    return response.data;
};

export const logActivity = async (activityData) => {
    try {
        const response = await api.post('/activity_feed.php', activityData);
        return response.data;
    } catch (err) {
        console.warn('Could not log activity to server:', err);
        return null;
    }
};

export const fetchReportsData = async (params = {}) => {
    const response = await api.get('/reports.php', { params });
    return response.data;
};

export const fetchNotifications = async (params = {}) => {
    const response = await api.get('/notifications.php', { params });
    return response.data;
};

export const markNotificationRead = async (id) => {
    const response = await api.post('/notifications.php', { action: 'mark_read', id });
    return response.data;
};

export const markAllNotificationsRead = async () => {
    const response = await api.post('/notifications.php', { action: 'mark_all_read' });
    return response.data;
};

export const loginUser = async (email, password) => {
    const response = await api.post('/login.php', { email, password });
    return response.data;
};

export default api;
